import { BaseElement } from 'base';
import { bus } from 'bus';
import { sectionFragment } from 'section-fragment';
import { SLIDE_CART_TOPIC } from 'slide-cart-topic';

const FOCUS_TARGETS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])"
];
const FOCUSABLE = FOCUS_TARGETS.map(
  target => `.slide-cart__panel ${target}`
).join(', ');
const FOCUS_TARGET = FOCUS_TARGETS.join(', ');

const STAGED = 'data-slide-cart-staged';
const PANEL = ':scope > .slide-cart__panel';
const HEADER = `${PANEL} > drawer-header:not([${STAGED}])`;
const SCROLL_AREA = `${PANEL} > .slide-cart__scroll-area:not([${STAGED}])`;
const BODY = `${SCROLL_AREA} > .slide-cart__body`;
const FOOTER = `${PANEL} > .slide-cart__footer:not([${STAGED}])`;
const MESSAGES = `${BODY} > .slide-cart__messages:not([${STAGED}])`;
const CARDS = `${BODY} > .slide-cart__cards:not([${STAGED}])`;
const SELF_RENDERED = [
  `${BODY} > .slide-cart__carousel:not([${STAGED}])`,
  `${BODY} > .slide-cart__summary:not([${STAGED}])`
];
const STYLE_WAIT_MS = 1500;

class SlideCart extends BaseElement {
  #action = null;
  opener = null;
  #opened = false;
  #requestedOpener = null;
  #active = null;
  #latest = null;
  #rendering = Promise.resolve();
  #styleWaiters = new Map();
  #loading = false;
  #loaderShown = false;
  #loadingFocus = null;
  #queued = 0;
  #committed = null;

  onConnect() {
    if (this.dataset.sectionId && this.id) {
      this.#styleWaiters = new Map();
      this.listen(this, 'load', this.#onStyleSettled, true);
      this.listen(this, 'error', this.#onStyleSettled, true);
      this.cleanup(() => this.#styleWaiters.clear());
      this.subscribeLive('cart:updated', this.#onCartUpdated);
    }

    if (this.dataset.presentation !== 'drawer') return;

    this.listen(this.q('[data-slide-cart-scrim]'), 'click', () =>
      this.requestDismiss('scrim')
    );
    this.listen(this, 'keydown', this.onKeydown);

    this.#bindHeader();

    this.subscribeLive(SLIDE_CART_TOPIC, this.#onActive);

    if (!this.id)
      console.error(
        '[slide-cart] a drawer without an id cannot be opened through the bus:',
        this
      );

    if (this.dataset.open === 'true') {
      if (this.id) bus.publish(SLIDE_CART_TOPIC, this.id);
      else this.open();
    }
  }

  onDisconnect() {
    if (this.dataset.presentation !== 'drawer') return;

    if (this.#opened) {
      this.#opened = false;
      document.documentElement.classList.remove('cart-drawer-open');
      this.opener = null;
    }
    if (this.id && this.#active === this.id)
      bus.publish(SLIDE_CART_TOPIC, null);
  }

  #bindHeader() {
    if (this.dataset.presentation !== 'drawer') return;
    const header = this.q(HEADER);
    if (!header) return;
    customElements.whenDefined('drawer-header').then(() => {
      if (!this.isConnected) return;
      header.run(() => this.requestDismiss('close-control'));
    });
  }

  #onCartUpdated({ sections } = {}) {
    if (!sections?.[this.dataset.sectionId]) return;
    this.#latest = sections;
    this.#queued += 1;
    this.#rendering = this.#rendering
      .then(() => {
        this.#queued -= 1;
        return this.#reconcile(sections);
      })
      .catch(error => console.error('[slide-cart] render failed:', error));
  }

  async #reconcile(sections) {
    if (!this.isConnected) return;
    const fresh = sectionFragment(sections, this.dataset.sectionId, this.id);
    const freshPanel = fresh?.querySelector(PANEL);
    const panel = this.q(PANEL);
    if (!freshPanel || !panel) return;
    if (!fresh.querySelector(SCROLL_AREA) || !this.q(SCROLL_AREA)) return;

    const pairs = [
      this.#pair(HEADER, fresh, node => panel.prepend(node)),
      ...(fresh.dataset.state === this.dataset.state
        ? this.#regionPairs(fresh, panel)
        : [
            this.#pair(SCROLL_AREA, fresh, node => panel.append(node), false),
            this.#pair(FOOTER, fresh, node => panel.append(node), false)
          ])
    ];
    if (!(await this.#replace(pairs, sections))) return;

    this.#committed = sections;
    this.#bindHeader();
    this.dataset.state = fresh.dataset.state;
    this.#syncBusy();
    if (this.#loading) this.#renderLoading();
  }

  #renderLoading() {
    this.#loaderShown = this.#loading;
    this.#syncBusy();
    this.q(HEADER)?.setCountHidden?.(this.#loading);
  }

  #syncBusy() {
    const panel = this.q(PANEL);
    if (!panel) return;
    if (this.#loaderShown || this.dataset.state === 'loading')
      panel.setAttribute('aria-busy', 'true');
    else panel.removeAttribute('aria-busy');
  }

  #regionPairs(fresh, panel) {
    const body = this.q(BODY);
    const pairs = [
      this.#pair(MESSAGES, fresh, node => body.prepend(node)),
      this.#pair(CARDS, fresh, node => {
        const messages = this.q(MESSAGES);
        if (messages) messages.after(node);
        else body.prepend(node);
      })
    ];
    for (const region of SELF_RENDERED) {
      if (this.q(`${region} :not(:defined)`))
        pairs.push(this.#pair(region, fresh, () => {}));
    }
    pairs.push(this.#pair(FOOTER, fresh, node => panel.append(node)));
    return pairs;
  }

  #pair(selector, fresh, place, keepFocus = true) {
    return {
      current: this.q(selector),
      next: fresh.querySelector(selector),
      place,
      keepFocus
    };
  }

  async #replace(pairs, sections) {
    const staged = [];
    for (const { current, next, place } of pairs) {
      if (!next) continue;
      next.setAttribute(STAGED, '');
      next.inert = true;
      if (current) current.before(next);
      else place(next);
      staged.push(next);
    }

    const styles = this.#stylesReady(
      this.qa(`[${STAGED}] link[rel='stylesheet']`)
    );
    await this.#activate(this.qa(`[${STAGED}] script[type='module'][src]`));
    await styles;
    if (!this.isConnected || this.#latest !== sections) {
      staged.forEach(node => node.remove());
      return false;
    }

    for (const { current, next, keepFocus } of pairs) {
      const path = this.#focusPath(current);
      current?.remove();
      if (next) {
        next.removeAttribute(STAGED);
        next.inert = false;
      }
      this.#restoreFocus(keepFocus ? next : null, path);
    }
    return true;
  }

  #activate(scripts) {
    return Promise.all(
      scripts.map(script =>
        import(script.src).catch(error =>
          console.error(
            '[slide-cart] module failed to load:',
            script.src,
            error
          )
        )
      )
    );
  }

  #stylesReady(links) {
    const pending = links
      .filter(link => !link.sheet)
      .map(
        link => new Promise(resolve => this.#styleWaiters.set(link, resolve))
      );
    if (pending.length === 0) return Promise.resolve();
    let timer;
    const timeout = new Promise(resolve => {
      timer = setTimeout(resolve, STYLE_WAIT_MS);
    });
    return Promise.race([Promise.all(pending), timeout]).finally(() => {
      clearTimeout(timer);
      links.forEach(link => this.#styleWaiters.delete(link));
    });
  }

  #onStyleSettled(event) {
    const resolve = this.#styleWaiters.get(event.target);
    if (!resolve) return;
    this.#styleWaiters.delete(event.target);
    resolve();
  }

  #focusPath(region) {
    let node = document.activeElement;
    if (!region || !node || !region.contains(node)) return null;
    const path = [];
    while (node !== region) {
      path.unshift([...node.parentElement.children].indexOf(node));
      node = node.parentElement;
    }
    return path;
  }

  #restoreFocus(region, path) {
    if (!path) return;
    const target = region ? this.#resolvePath(region, path) : null;
    if (target?.matches(FOCUS_TARGET) && !target.closest('[inert]')) {
      target.focus({ preventScroll: true });
      return;
    }
    this.#focusPanel();
  }

  #focusPanel() {
    if (this.dataset.presentation !== 'drawer') return;
    const panel = this.q(PANEL);
    if (panel && !panel.contains(document.activeElement))
      panel.focus({ preventScroll: true });
  }

  #resolvePath(region, path) {
    let node = region;
    for (const [depth, index] of path.entries()) {
      const children = node.children;
      if (children.length === 0) return null;
      node =
        children[depth === 0 ? Math.min(index, children.length - 1) : index];
      if (!node) return null;
    }
    return node;
  }

  #onActive(active) {
    this.#active = active;
    if (this.id && active === this.id) {
      this.open();
      return;
    }
    if (this.#opened) this.close({ unlock: active == null });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.requestDismiss('escape');
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = this.qa(FOCUSABLE).filter(
      element => element.offsetParent !== null && !element.closest('[inert]')
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !focusable.includes(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open(opener) {
    if (this.dataset.presentation !== 'drawer') return this;
    if (this.#opened) {
      this.#focusPanel();
      return this;
    }

    if (this.id && this.#active !== this.id) {
      if (!this.isConnected) return this;
      this.#requestedOpener = opener ?? null;
      bus.publish(SLIDE_CART_TOPIC, this.id);
      this.#requestedOpener = null;
      return this;
    }

    this.#opened = true;
    this.opener =
      opener ||
      this.#requestedOpener ||
      this.#openerFrom(document.activeElement);
    this.#requestedOpener = null;
    this.q(BODY)?.scrollTo({ top: 0 });
    this.dataset.open = 'true';

    this.q('[data-slide-cart-scrim]')?.removeAttribute('inert');
    const panel = this.q('[data-slide-cart-panel]');
    panel?.removeAttribute('inert');

    document.documentElement.classList.add('cart-drawer-open');

    const rafId = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      panel?.focus();
    });
    this.cleanup(() => cancelAnimationFrame(rafId));

    return this;
  }

  #openerFrom(active) {
    const host = active?.closest?.('slide-cart');
    if (!host || host === this || host.dataset.presentation !== 'drawer')
      return active;
    return host.opener ?? null;
  }

  close({ unlock = true } = {}) {
    if (this.dataset.presentation !== 'drawer' || !this.#opened) return this;

    if (this.id && this.#active === this.id) {
      bus.publish(SLIDE_CART_TOPIC, null);
      return this;
    }

    this.#opened = false;
    this.dataset.open = 'false';

    this.q('[data-slide-cart-scrim]')?.setAttribute('inert', '');
    this.q('[data-slide-cart-panel]')?.setAttribute('inert', '');

    if (unlock) document.documentElement.classList.remove('cart-drawer-open');
    this.opener?.focus?.();
    this.opener = null;

    return this;
  }

  run(action) {
    this.#action = action;
    return this;
  }

  setLoading(isLoading) {
    this.#loading = Boolean(isLoading);
    if (!this.#loading) {
      this.#queueLoadingOff();
      return this;
    }
    this.#renderLoading();
    const active = document.activeElement;
    const region = [BODY, FOOTER].find(selector =>
      this.q(selector)?.contains(active)
    );
    if (region) {
      this.#loadingFocus = {
        region,
        path: this.#focusPath(this.q(region)),
        state: this.dataset.state
      };
      this.q(PANEL)?.focus({ preventScroll: true });
    }
    return this;
  }

  #queueLoadingOff() {
    this.#rendering = this.#rendering
      .then(() => {
        if (!this.isConnected || this.#loading) return;
        if (this.#queued > 0 && this.#latest !== this.#committed) {
          this.#queueLoadingOff();
          return;
        }
        this.#renderLoading();
        this.#restoreLoadingFocus();
      })
      .catch(error => console.error('[slide-cart] render failed:', error));
  }

  #restoreLoadingFocus() {
    const saved = this.#loadingFocus;
    this.#loadingFocus = null;
    const panel = this.q(PANEL);
    if (!saved || !panel || document.activeElement !== panel) return;
    const region =
      saved.state === this.dataset.state ? this.q(saved.region) : null;
    this.#restoreFocus(region, saved.path);
  }

  requestDismiss(reason) {
    if (this.id && this.#active === this.id)
      bus.publish(SLIDE_CART_TOPIC, null);
    this.close();
    Promise.resolve(this.#action?.({ reason })).catch(error =>
      console.error('[slide-cart] dismiss action failed:', error)
    );
  }
}

if (!customElements.get('slide-cart'))
  customElements.define('slide-cart', SlideCart);
